/**
 * Branches Hook
 * React hook for fetching active branches from the Branch API
 * Uses smart caching to avoid duplicate API calls across components
 * 
 * Supports including an inactive branch by code (for viewing engagements with deactivated branches)
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useApi } from '@/lib/api'

export interface Branch {
  Id: number
  Code: string
  Name: string
  IsActive: boolean
}

interface BranchesApiResponse {
  Items: Branch[]
  Count: number
  NextPageLink: string | null
}

interface CachedBranches {
  branches: Branch[]
  loading: boolean
  error: string | null
  timestamp: number
}

interface UseBranchesOptions {
  /**
   * Branch code to include even if inactive (for viewing existing engagements)
   * The hook will fetch this branch separately if not found in active list
   */
  includeInactiveBranchCode?: string
}

interface UseBranchesReturn {
  // Loading state
  loading: boolean
  
  // Error state
  error: string | null
  
  // Data
  branches: Branch[]
  
  // Actions
  refresh: () => Promise<void>
}

// Global cache outside the hook - shared across all instances
const branchesCache: CachedBranches = {
  branches: [],
  loading: false,
  error: null,
  timestamp: 0
}

// Cache for individual inactive branches (by code)
const inactiveBranchCache: Map<string, Branch> = new Map()

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000

// Set of subscribers for cache updates
const subscribers = new Set<() => void>()

// Notify all subscribers when cache updates
const notifySubscribers = () => {
  subscribers.forEach(callback => {
    try {
      callback()
    } catch (err) {
      console.error('Branch cache subscriber error:', err)
    }
  })
}

// Track pending request to avoid duplicates
let pendingRequest: Promise<void> | null = null

// Track pending inactive branch requests
const pendingInactiveRequests: Map<string, Promise<Branch | null>> = new Map()

export function useBranches(options: UseBranchesOptions = {}): UseBranchesReturn {
  const { includeInactiveBranchCode } = options
  const { mainApi } = useApi()
  const [updateCount, incrementUpdateCount] = useState(0)
  const [inactiveBranch, setInactiveBranch] = useState<Branch | null>(null)
  
  // Subscribe to cache updates
  useEffect(() => {
    const updateCallback = () => incrementUpdateCount(prev => prev + 1)
    subscribers.add(updateCallback)
    
    return () => {
      subscribers.delete(updateCallback)
    }
  }, [])

  // Fetch branches from API
  const fetchBranches = useCallback(async (): Promise<Branch[]> => {
    
    const params = new URLSearchParams({
      '$filter': 'IsActive eq true',
      '$select': 'Id,Code,Name,IsActive'
    })
    
    const response = await mainApi.get<BranchesApiResponse>(`api/branch/list?${params.toString()}`)
    
    const branches = response?.Items || []
    return branches
  }, [mainApi])

  // Fetch a specific branch by code (for inactive branches)
  const fetchBranchByCode = useCallback(async (code: string): Promise<Branch | null> => {
    // Check cache first
    if (inactiveBranchCache.has(code)) {
      return inactiveBranchCache.get(code)!
    }
    
    // Check if there's already a pending request for this code
    if (pendingInactiveRequests.has(code)) {
      return pendingInactiveRequests.get(code)!
    }
    
    const request = (async () => {
      try {
        const params = new URLSearchParams({
          '$filter': `Code eq '${code}'`,
          '$select': 'Id,Code,Name,IsActive'
        })
        
        const response = await mainApi.get<BranchesApiResponse>(`api/branch/list?${params.toString()}`)
        const branch = response?.Items?.[0] || null
        
        if (branch) {
          inactiveBranchCache.set(code, branch)
        }
        
        return branch
      } catch (err) {
        console.error(`Failed to fetch branch with code ${code}:`, err)
        return null
      } finally {
        pendingInactiveRequests.delete(code)
      }
    })()
    
    pendingInactiveRequests.set(code, request)
    return request
  }, [mainApi])

  // Fetch and cache function
  const fetchAndCache = useCallback(async () => {
    // Return existing pending request to avoid duplicates
    if (pendingRequest) {
      return pendingRequest
    }
    
    // Set loading state in cache
    branchesCache.loading = true
    branchesCache.error = null
    notifySubscribers()
    
    // Create pending request
    pendingRequest = (async () => {
      try {
        const branches = await fetchBranches()
        
        // Cache the successful results
        branchesCache.branches = branches
        branchesCache.loading = false
        branchesCache.error = null
        branchesCache.timestamp = Date.now() 
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load branches'
        
        // Set error state but don't update timestamp - allows immediate retry on next mount
        branchesCache.branches = []
        branchesCache.loading = false
        branchesCache.error = errorMessage
        
        console.error('Failed to load branches:', err)
      } finally {
        pendingRequest = null
        notifySubscribers()
      }
    })()
    
    return pendingRequest
  }, [fetchBranches])

  const refresh = useCallback(async () => {
    await fetchAndCache()
  }, [fetchAndCache])

  // Check if data needs to be fetched
  useEffect(() => {
    const now = Date.now()
    
    // Fetch if cache is empty, expired, or has an error
    if (!branchesCache.timestamp || 
        (now - branchesCache.timestamp) > CACHE_TTL ||
        branchesCache.error) {
      fetchAndCache()
    }
  }, [fetchAndCache])

  // Fetch inactive branch if needed
  useEffect(() => {
    if (!includeInactiveBranchCode || branchesCache.loading) {
      return
    }
    
    // Check if the branch is already in the active list
    const existsInActiveList = branchesCache.branches.some(
      b => b.Code === includeInactiveBranchCode
    )
    
    if (existsInActiveList) {
      setInactiveBranch(null)
      return
    }
    
    // Fetch the inactive branch
    fetchBranchByCode(includeInactiveBranchCode).then(branch => {
      setInactiveBranch(branch)
    })
  }, [includeInactiveBranchCode, fetchBranchByCode, branchesCache.loading])

  // Combine active branches with the inactive branch if needed
  // Note: updateCount is used to trigger recalculation when branchesCache updates
  // since branchesCache is outside React's state system
  const branches = useMemo(() => {
    if (!inactiveBranch) {
      return branchesCache.branches
    }
    
    // Check if already in the list (in case it was reactivated)
    const alreadyExists = branchesCache.branches.some(
      b => b.Code === inactiveBranch.Code
    )
    
    if (alreadyExists) {
      return branchesCache.branches
    }
    
    // Add the inactive branch to the list
    return [...branchesCache.branches, inactiveBranch]
  }, [inactiveBranch, updateCount])

  return {
    // Loading state
    loading: branchesCache.loading,
    
    // Error state
    error: branchesCache.error,
    
    // Data
    branches,
    
    // Actions
    refresh
  }
}

